/**
 * AddToHomeScreenCard — PWA install affordance for the More page.
 *
 * Three states this handles:
 *   1. App is already installed (standalone display mode) → render nothing.
 *   2. Chrome/Edge/Android: capture `beforeinstallprompt`, render an
 *      "Install Chalk OS" CTA that fires the native prompt.
 *   3. iOS Safari (no install event ever fires): render a short tap-through
 *      explainer that says "Tap Share → Add to Home Screen" with the iOS
 *      Share glyph hint.
 *
 * The card hides itself on a successful install via the `appinstalled` event.
 */
import { useEffect, useState } from "react";
import { Download, Share, Smartphone } from "lucide-react";
import { toast } from "sonner";

import { BottomSheet, ChalkActionButton, ChalkCard } from "./primitives";

function _isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
    return true;
  }
  // iOS Safari standalone flag
  return Boolean(window.navigator && window.navigator.standalone);
}

function _isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export default function AddToHomeScreenCard() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(_isStandalone());
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const isIOS = _isIOS();

  useEffect(() => {
    function onBeforePrompt(e) {
      e.preventDefault();
      setDeferred(e);
    }
    function onInstalled() {
      setInstalled(true);
      setDeferred(null);
    }
    window.addEventListener("beforeinstallprompt", onBeforePrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforePrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Already installed? Nothing to show.
  if (installed) return null;
  // Not iOS and no install prompt captured (probably installed via Edge
  // sidebar or in a webview that doesn't expose the event) → hide card to
  // avoid dangling CTA.
  if (!isIOS && !deferred) return null;

  async function install() {
    if (!deferred) return;
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        toast.success("Installed. Open Chalk from your home screen anytime.");
      }
      setDeferred(null);
    } catch (e) {
      toast.error(e.message || "Couldn't install. Try again from your browser menu.");
    }
  }

  return (
    <>
      <ChalkCard
        data-testid="install-app-card"
        onClick={isIOS ? () => setIosSheetOpen(true) : install}
        style={{ cursor: "pointer", marginTop: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "rgba(46,125,82,0.10)",
            color: "var(--green-mid)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            {isIOS ? <Smartphone size={18} aria-hidden="true" />
                   : <Download size={18} aria-hidden="true" />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, color: "var(--chalk-board)", fontWeight: 500 }}>
              Add Chalk to your home screen
            </div>
            <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4, lineHeight: 1.4 }}>
              {isIOS
                ? "Get one-tap access from your iPhone home screen — quick steps inside."
                : "Install Chalk OS like a native app — no app store needed."}
            </div>
          </div>
        </div>
      </ChalkCard>

      <BottomSheet isOpen={iosSheetOpen} title="Install on iPhone" onDismiss={() => setIosSheetOpen(false)}>
        <div data-testid="install-ios-sheet" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 14, color: "var(--chalk-board)", lineHeight: 1.5 }}>
            iPhone needs Safari's <strong>Share</strong> menu to install web apps.
            Three taps:
          </div>
          <IOSInstallSteps />
          <ChalkActionButton
            testId="install-ios-done"
            variant="primary"
            label="Got it"
            onClick={() => setIosSheetOpen(false)}
          />
        </div>
      </BottomSheet>
    </>
  );
}

function Step({ n, icon, title, body }) {
  return (
    <div style={{
      display: "flex", gap: 12, alignItems: "flex-start",
      background: "rgba(46,125,82,0.04)",
      border: "0.5px solid rgba(46,125,82,0.15)",
      borderRadius: 10, padding: "12px 14px",
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: "#fff", color: "var(--green-mid)",
        border: "0.5px solid rgba(46,125,82,0.2)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 11, textTransform: "uppercase", letterSpacing: "0.10em",
          color: "var(--chalk-muted)", fontWeight: 700,
        }}>
          Step {n}
        </div>
        <div style={{ fontSize: 14, color: "var(--chalk-board)", fontWeight: 600, marginTop: 2 }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 4, lineHeight: 1.4 }}>
          {body}
        </div>
      </div>
    </div>
  );
}

/** The three Safari steps — shared with the public tournament "Follow my team" sheet. */
export function IOSInstallSteps() {
  return (
    <>
      <Step
        n={1}
        icon={<Share size={18} aria-hidden="true" />}
        title="Tap the Share button"
        body="At the bottom of Safari (the square with the up arrow)."
      />
      <Step
        n={2}
        icon={<Smartphone size={18} aria-hidden="true" />}
        title='Scroll → "Add to Home Screen"'
        body='In the share sheet, find "Add to Home Screen".'
      />
      <Step
        n={3}
        icon={<Download size={18} aria-hidden="true" />}
        title='Tap "Add"'
        body="The Chalk icon lands on your home screen. Tap it like any app."
      />
    </>
  );
}
